import { Component, Input } from '@angular/core';

export interface LineChartPoint {
  label: string;
  value: number;
}

@Component({
  selector: 'app-line-chart',
  standalone: false,
  templateUrl: './line-chart.html',
})
export class LineChart {
  @Input() points: LineChartPoint[] = [];
  @Input() dateLabels = true;
  @Input() valueLabel = 'requests';

  private readonly CHART_PAD_PCT = 8;

  hoveredIndex: number | null = null;

  get chartMax(): number {
    const max = Math.max(...this.points.map(p => p.value), 0);
    return this.niceMax(max);
  }

  private niceMax(value: number): number {
    if (value <= 0) return 1;
    const pow = Math.pow(10, Math.floor(Math.log10(value)));
    for (const step of [1, 2, 2.5, 5, 10]) {
      if (value <= step * pow) return step * pow;
    }
    return 10 * pow;
  }

  chartYTicks(): number[] {
    const max = this.chartMax;
    return [max, max / 2, 0];
  }

  chartPointXPct(i: number): number {
    const n = this.points.length - 1;
    return n > 0 ? (i / n) * 100 : 0;
  }

  chartPointYPct(value: number): number {
    const usable = 100 - this.CHART_PAD_PCT * 2;
    const frac = this.chartMax ? value / this.chartMax : 0;
    return this.CHART_PAD_PCT + usable * (1 - frac);
  }

  gridYPct(tick: number): number {
    return this.chartPointYPct(tick);
  }

  chartLinePoints(): string {
    return this.points
      .map((p, i) => `${this.chartPointXPct(i)},${this.chartPointYPct(p.value)}`)
      .join(' ');
  }

  thinnedPoints(): LineChartPoint[] {
    const step = Math.max(1, Math.ceil(this.points.length / 15));
    return this.points.filter((_, i) => i % step === 0);
  }

  onPointHover(i: number): void {
    this.hoveredIndex = i;
  }

  onChartLeave(): void {
    this.hoveredIndex = null;
  }

  get hoveredPoint(): LineChartPoint | null {
    return this.hoveredIndex != null ? (this.points[this.hoveredIndex] ?? null) : null;
  }
}
